"use client";

import { MessageCircle } from "lucide-react";
import { motion } from "motion/react";
import { useSiteContact } from "@/hooks/use-site-contact";

/**
 * FloatingWhatsApp Component
 *
 * Fixed position WhatsApp button for quick customer contact.
 * Critical for GCC market lead generation.
 *
 * Features:
 * - Fixed bottom-right position
 * - Subtle pulse animation to draw attention
 * - Opens WhatsApp with pre-filled message (from CMS)
 * - Accessible with proper ARIA labels
 * - Respects reduced motion preference
 * - Falls back to static data if CMS unavailable
 */
export function FloatingWhatsApp() {
	const { data: contact } = useSiteContact();

	// Build WhatsApp URL with pre-filled message
	const message = encodeURIComponent(
		"Hello! I'm interested in your interior design services.",
	);
	const whatsappUrl = contact.whatsappHref
		? `${contact.whatsappHref}?text=${message}`
		: `https://wa.me/${contact.phone.replace(/\D/g, "")}?text=${message}`;

	return (
		<motion.a
			animate={{ scale: 1, opacity: 1 }}
			aria-label="Contact us on WhatsApp"
			className="group fixed right-6 bottom-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp text-white shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-whatsapp focus:ring-offset-2"
			href={whatsappUrl}
			initial={{ scale: 0, opacity: 0 }}
			rel="noopener noreferrer"
			target="_blank"
			transition={{ type: "spring", stiffness: 260, damping: 20, delay: 1 }}
			whileHover={{ scale: 1.1 }}
			whileTap={{ scale: 0.95 }}
		>
			{/* Pulse ring animation */}
			<span
				aria-hidden="true"
				className="absolute inset-0 animate-ping rounded-full bg-whatsapp opacity-30 motion-reduce:animate-none"
			/>
			<MessageCircle className="h-6 w-6" />
		</motion.a>
	);
}
